/**
 * Merge assistant message annotations across stream updates and finalize.
 *
 * Streaming builds client-only positional keys (`render_segments`,
 * `tool_timeline`, `tool_first_seen_order`) that the server never emits.
 * Finalize / DB rows often omit them — without preserving from the live
 * annotation, Thought + tool sections vanish the moment streaming ends.
 */

import { mergeLosiSteps } from '@/lib/chat/losi-steps-merge'
import type { Json } from '@/supabase/types'

function isAnnotationRecord(value: unknown): value is Record<string, Json> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function hasReasoningText(value: unknown): boolean {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  const text = (value as Record<string, unknown>).text
  return typeof text === 'string' && text.trim().length > 0
}

function isNonEmptyArray(value: unknown): value is unknown[] {
  return Array.isArray(value) && value.length > 0
}

function preserveClientArray(
  merged: Record<string, Json>,
  current: Record<string, Json>,
  next: Record<string, Json>,
  key: 'render_segments' | 'tool_timeline' | 'tool_first_seen_order'
) {
  if (isNonEmptyArray(current[key]) && !isNonEmptyArray(next[key])) {
    merged[key] = current[key]
  }
}

export function mergeAssistantMessageAnnotation(
  currentAnnotation: Json | null | undefined,
  nextAnnotation: Json | null | undefined
): Json | null {
  if (nextAnnotation == null) {
    return currentAnnotation ?? null
  }

  if (!isAnnotationRecord(currentAnnotation) || !isAnnotationRecord(nextAnnotation)) {
    return nextAnnotation
  }

  const merged: Record<string, Json> = {
    ...currentAnnotation,
    ...nextAnnotation
  }

  // Only drop the live stream summary once a real finalized summary exists.
  // An empty/placeholder `reasoning_summary` used to wipe Thought on complete.
  //
  // Prefer next's live stream when present — processResponse merges the full
  // accumulated annotation each chunk, and an older preserve-current branch
  // froze Thought on the first reasoning_summary_stream frame.
  if (hasReasoningText(nextAnnotation.reasoning_summary)) {
    delete merged.reasoning_summary_stream
  } else if (hasReasoningText(nextAnnotation.reasoning_summary_stream)) {
    merged.reasoning_summary_stream = nextAnnotation.reasoning_summary_stream
    if (!hasReasoningText(nextAnnotation.reasoning_summary)) {
      delete merged.reasoning_summary
    }
  } else if (hasReasoningText(currentAnnotation.reasoning_summary_stream)) {
    merged.reasoning_summary_stream = currentAnnotation.reasoning_summary_stream
    if (!hasReasoningText(nextAnnotation.reasoning_summary)) {
      delete merged.reasoning_summary
    }
  } else if (
    !hasReasoningText(merged.reasoning_summary) &&
    hasReasoningText(currentAnnotation.reasoning_summary)
  ) {
    merged.reasoning_summary = currentAnnotation.reasoning_summary
  }

  preserveClientArray(merged, currentAnnotation, nextAnnotation, 'render_segments')
  preserveClientArray(merged, currentAnnotation, nextAnnotation, 'tool_timeline')
  preserveClientArray(merged, currentAnnotation, nextAnnotation, 'tool_first_seen_order')

  if ('losi_steps' in nextAnnotation) {
    merged.losi_steps = mergeLosiSteps(
      currentAnnotation.losi_steps,
      nextAnnotation.losi_steps
    )
  }

  return merged as Json
}

/**
 * Prefer the live streaming message annotation, then fall back to the temp
 * seed, then merge in the streamed `data` payload for persist/finalize.
 */
export function resolveAssistantAnnotationForPersist(args: {
  chatMessages: Array<{
    message: {
      id?: string | null
      role?: string | null
      sequence_number?: number | null
      annotation?: Json | null
    }
  }>
  tempAssistantMessage: {
    id?: string | null
    role?: string | null
    sequence_number?: number | null
    annotation?: Json | null
  }
  streamedAnnotation: Json | null | undefined
}): Json | null {
  const { chatMessages, tempAssistantMessage, streamedAnnotation } = args
  const live = chatMessages.find(
    (chatMessage) =>
      chatMessage.message.id === tempAssistantMessage.id ||
      (chatMessage.message.role === 'assistant' &&
        chatMessage.message.sequence_number === tempAssistantMessage.sequence_number)
  )

  return mergeAssistantMessageAnnotation(
    (live?.message.annotation as Json | null | undefined) ??
      (tempAssistantMessage.annotation as Json | null | undefined) ??
      null,
    streamedAnnotation ?? null
  )
}
